"use client";

import { useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "next/navigation";
import { TierSelectionPage } from "@/components/tier/selection/tier-selection-page";
import { hiivesKeys } from "@/features/hiives/queries/keys";
import { HIIVE_JOIN_LOAD_ERROR_MESSAGE } from "./hiive-join-section.shared";
import type { HiiveJoinSectionData } from "./hiive-join-section.types";

type HiiveJoinSectionClientProps = {
  initialData: HiiveJoinSectionData;
};

export function HiiveJoinSectionClient({
  initialData,
}: HiiveJoinSectionClientProps) {
  const params = useParams<{ hiiveSlug: string }>();
  const searchParams = useSearchParams();
  const hiiveSlug = params.hiiveSlug ?? "";
  const hasHiiveSlug = hiiveSlug.length > 0;
  const forEventSlug = searchParams.get("forEvent");
  const invalidJoinKey = [...hiivesKeys.joinAll(), "page", "invalid"] as const;

  const hiiveJoinQuery = useQuery<HiiveJoinSectionData>({
    queryKey: hasHiiveSlug
      ? hiivesKeys.joinPage(hiiveSlug, forEventSlug)
      : invalidJoinKey,
    queryFn: async () => initialData,
    initialData,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: hasHiiveSlug,
  });

  const hiiveJoin = hiiveJoinQuery.data ?? initialData;

  if (!hasHiiveSlug || !hiiveJoin || hiiveJoinQuery.error) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center bg-background px-4 text-center">
        <p className="text-sm text-text-secondary">
          {HIIVE_JOIN_LOAD_ERROR_MESSAGE}
        </p>
      </div>
    );
  }

  return (
    <TierSelectionPage
      hiiveId={hiiveJoin.hiiveId}
      hiiveSlug={hiiveJoin.hiiveSlug}
      hiiveName={hiiveJoin.hiiveName}
      tiers={hiiveJoin.tiers}
      membershipState={hiiveJoin.membershipState}
      pendingJoinRequest={hiiveJoin.pendingJoinRequest}
      currentMembership={hiiveJoin.currentMembership}
      isAuthenticated={hiiveJoin.isAuthenticated}
      profileComplete={hiiveJoin.profileComplete}
      hasDeliverableEmail={hiiveJoin.hasDeliverableEmail}
      contactEmail={hiiveJoin.contactEmail}
      eventContext={hiiveJoin.eventContext}
      nextSteps={hiiveJoin.nextSteps}
      previewMode={searchParams.get("preview") === "offerings"}
    />
  );
}
